import { useEffect } from "react";
import Tabs from "@/components/Tabs";
import { useDenyAuthorization } from "@/hooks/useCapabilities";
import AppLayout from "@/layouts/AppLayout";
import PageMessage from "@/components/PageMessage";
import { applyBackendSource } from "@/lib/backendSource";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useConfigurationAccess, useInteractionTopicsAccess } from "./access";
import { shouldSwitchConfigurationToLocalBackend } from "./localBackend";

const GLOBAL_ADMIN_TAB_ITEMS = [
  { label: "Career Consultants", to: "/configuration/career-consultants" },
  { label: "Player Engagement", to: "/configuration/player-engagement" },
  { label: "Recruiters", to: "/configuration/recruiters" }
] as const;

const INTERACTION_TOPICS_TAB_ITEM = { label: "Interaction Topics", to: "/configuration/interaction-topics" } as const;

function ConfigurationTabs({ showGlobalTabs, showTopicsTab }: { showGlobalTabs: boolean; showTopicsTab: boolean }) {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const normalizedPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const tabItems = [
    ...(showGlobalTabs ? GLOBAL_ADMIN_TAB_ITEMS : []),
    ...(showTopicsTab ? [INTERACTION_TOPICS_TAB_ITEM] : [])
  ];

  return (
    <Tabs
      tabItems={tabItems.map(({ label, to }) => ({
        label,
        onClick: () => navigate(to as Path),
        active: normalizedPath === to
      }))}
    />
  );
}

export default function TabLayout({ children }: { children: React.ReactNode }) {
  const denyAuthorization = useDenyAuthorization();
  const { loaded, canView: canViewGlobal } = useConfigurationAccess();
  const topicsAccess = useInteractionTopicsAccess();
  const location = useLocation();
  const normalizedPath = location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname;
  const isTopicsRoute = normalizedPath === INTERACTION_TOPICS_TAB_ITEM.to;
  // Department admins may only see the Interaction Topics tab; every other
  // configuration surface stays global-admin-only (including direct URLs).
  const canView = isTopicsRoute ? topicsAccess.canView : canViewGlobal;
  const switchToLocalBackend = loaded && canView && shouldSwitchConfigurationToLocalBackend();

  useEffect(() => {
    if (switchToLocalBackend) {
      applyBackendSource("localhost");
    }
  }, [switchToLocalBackend]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (!canView) {
      denyAuthorization();
    }
  }, [loaded, canView, denyAuthorization]);

  if (switchToLocalBackend) {
    return (
      <PageMessage
        title="Switching to your local API"
        message="Member 360 Configuration isn't on the shared-dev API yet."
      />
    );
  }

  if (!loaded || !canView) {
    return null;
  }

  return (
    <AppLayout>
      {/* Sticky level 1: margins collapse away when stuck, so top targets the
          border box flush under the 77px top action bar. */}
      <div className="sticky top-[77px] z-40 bg-sys-brand-surface">
        <ConfigurationTabs showGlobalTabs={canViewGlobal} showTopicsTab={topicsAccess.canView} />
      </div>
      <div className="flex flex-col">{children}</div>
    </AppLayout>
  );
}
